import { FC } from 'react';
import { createPortal } from 'react-dom';
import { Box, Divider, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import {
  GlobalIcon,
  CalendarIcon,
  TripStartIcon,
  RecentlyEditedIcon,
  RecentlyCreatedIcon,
} from '../../../../../components/Icons';

import styles from './TripsFiltersDrawer.module.scss';
import CustomSwitch from '../../../../../common/CustomSwitch';

export interface TripsFilters {
  displayFormat: 'map' | 'calendar';
  sortBy: 'start_date' | 'created_at' | 'edited_at';
  sortOrder: 'asc' | 'desc';
}

interface TripsFiltersDrawerProps {
  openFilters: boolean;
  toggleFilters: (open: boolean) => void;
  filters: TripsFilters;
  onFiltersChange: (filters: TripsFilters) => void;
}

const TripsFiltersDrawer: FC<TripsFiltersDrawerProps> = ({ openFilters, toggleFilters, filters, onFiltersChange }) => {
  const toggleDisplayFormat = () => {
    const newFormat = filters.displayFormat === 'map' ? 'calendar' : 'map';
    onFiltersChange({
      ...filters,
      displayFormat: newFormat,
    });
  };

  const handleSortByChange = (field: 'start_date' | 'created_at' | 'edited_at') => (checked: boolean) => {
    if (checked) {
      onFiltersChange({
        ...filters,
        sortBy: field,
        sortOrder: field === 'start_date' ? 'asc' : 'desc',
      });
    }
  };

  if (!openFilters) {
    return null;
  }

  return createPortal(
    <Box className={styles.customDrawer}>
      <Box className={styles.customDrawerHeader}>
        <Typography
          variant="h5Bold"
          sx={{ color: '#222B3A' }}
        >
          Filters
        </Typography>
        <CloseIcon
          className={styles.closeIcon}
          onClick={() => toggleFilters(false)}
        />
      </Box>

      <Box className={styles.customDrawerContent}>
        <Box className={styles.filterSection}>
          <Typography
            variant="h9Bold"
            className={styles.sectionTitle}
          >
            Display format
          </Typography>

          <Box className={styles.filterOptions}>
            <CustomSwitch
              label="Map"
              checked={filters.displayFormat === 'map'}
              onChange={toggleDisplayFormat}
              icon={<GlobalIcon size={24} />}
            />
            <CustomSwitch
              label="Calendar"
              checked={filters.displayFormat === 'calendar'}
              onChange={toggleDisplayFormat}
              icon={<CalendarIcon size={24} />}
            />
          </Box>
        </Box>

        <Divider sx={{ borderColor: '#DDDDDD', mb: '16px', mt: '16px' }} />

        <Box className={styles.filterSection}>
          <Typography
            variant="h9Bold"
            className={styles.sectionTitle}
          >
            Sort by
          </Typography>

          <Box className={styles.filterOptions}>
            <CustomSwitch
              label="Trip Start Date"
              checked={filters.sortBy === 'start_date'}
              onChange={handleSortByChange('start_date')}
              icon={<TripStartIcon size={24} />}
            />
            <CustomSwitch
              label="Recently Edited"
              checked={filters.sortBy === 'edited_at'}
              onChange={handleSortByChange('edited_at')}
              icon={<RecentlyEditedIcon size={24} />}
            />
            <CustomSwitch
              label="Recently Created"
              checked={filters.sortBy === 'created_at'}
              onChange={handleSortByChange('created_at')}
              icon={<RecentlyCreatedIcon size={24} />}
            />
          </Box>
        </Box>
      </Box>
    </Box>,
    document.body
  );
};

export default TripsFiltersDrawer;
